import type { Discipline, DisciplineVariant } from '../types';

export interface DisciplineConfig {
  discipline: Discipline;
  variant: DisciplineVariant;
  label: string;
  description: string;
  stationCount: number;
  shotsPerStation: number;
  totalShotsPerRound: number;
  hasDoubles: boolean;
  hasHighLow: boolean;
  trackTrajectory: boolean;
  rotationPattern: 'right' | 'sequential' | 'none';
}

export interface DisciplineGroup {
  discipline: Discipline;
  label: string;
  icon: string;
  description: string;
  variants: DisciplineConfig[];
}

const trapVariants: DisciplineConfig[] = [
  {
    discipline: 'trap',
    variant: 'american-trap',
    label: 'American Trap',
    description: 'Standard ATA trap — 5 stations, 5 shots each, rotate right',
    stationCount: 5,
    shotsPerStation: 5,
    totalShotsPerRound: 25,
    hasDoubles: false,
    hasHighLow: false,
    trackTrajectory: true,
    rotationPattern: 'right',
  },
  {
    discipline: 'trap',
    variant: 'ata-singles',
    label: 'ATA Singles',
    description: 'Official ATA singles — same as American Trap',
    stationCount: 5,
    shotsPerStation: 5,
    totalShotsPerRound: 25,
    hasDoubles: false,
    hasHighLow: false,
    trackTrajectory: true,
    rotationPattern: 'right',
  },
  {
    discipline: 'trap',
    variant: 'ata-doubles',
    label: 'ATA Doubles',
    description: 'Two targets per pull — 5 stations, 50 targets per round',
    stationCount: 5,
    shotsPerStation: 10,
    totalShotsPerRound: 50,
    hasDoubles: true,
    hasHighLow: false,
    trackTrajectory: true,
    rotationPattern: 'right',
  },
];

const skeetVariants: DisciplineConfig[] = [
  {
    discipline: 'skeet',
    variant: 'american-skeet',
    label: 'American Skeet',
    description: '8 stations — high house, low house, doubles at 1,2,6,7',
    stationCount: 8,
    shotsPerStation: 0, // Variable per station
    totalShotsPerRound: 25,
    hasDoubles: true,
    hasHighLow: true,
    trackTrajectory: false,
    rotationPattern: 'sequential',
  },
];

const fiveStandVariants: DisciplineConfig[] = [
  {
    discipline: 'sporting-clays',
    variant: 'five-stand',
    label: 'Five-Stand',
    description: '5 fixed stations, multiple target machines — 25 targets',
    stationCount: 5,
    shotsPerStation: 5,
    totalShotsPerRound: 25,
    hasDoubles: true,
    hasHighLow: false,
    trackTrajectory: false,
    rotationPattern: 'sequential',
  },
];

export const disciplineGroups: DisciplineGroup[] = [
  {
    discipline: 'trap',
    label: 'Trap',
    icon: '🎯',
    description: 'Targets launched away from the shooter',
    variants: trapVariants,
  },
  {
    discipline: 'skeet',
    label: 'Skeet',
    icon: '🏹',
    description: 'Targets cross in front from high and low houses',
    variants: skeetVariants,
  },
  {
    discipline: 'sporting-clays',
    label: 'Five Stand',
    icon: '🌲',
    description: '5 fixed stations with multiple target presentations',
    variants: fiveStandVariants,
  },
];

export function getDisciplineConfig(variant: DisciplineVariant): DisciplineConfig | undefined {
  for (const group of disciplineGroups) {
    const config = group.variants.find(v => v.variant === variant);
    if (config) return config;
  }
  return undefined;
}

export function getDisciplineGroup(discipline: Discipline): DisciplineGroup | undefined {
  return disciplineGroups.find(g => g.discipline === discipline);
}

export function getDefaultVariant(discipline: Discipline): DisciplineVariant {
  switch (discipline) {
    case 'trap': return 'american-trap';
    case 'skeet': return 'american-skeet';
    case 'sporting-clays': return 'five-stand';
  }
}
